import {i18n} from '@online-offer/i18n';
import {Box, HStack, Icon, Text, ThemeColorTextPath, VStack} from 'platform/foundation';
import {LocaleDateTimeFormat, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {parseDate} from 'shared/web';

import {CardOo} from '../../components/CardOo/CardOo';
import {ThemeColorPath} from '../../theme/theme';
import {Timer} from './Timer';

export type TimerState = 'reserved' | 'soon_expired' | 'expired';

export type TimerCardColorSchema = {
  cardBg: ThemeColorPath;
  textColor: ThemeColorTextPath;
};

const getLabel = (timerState: TimerState): string =>
  match<TimerState, string>(timerState)
    .with('reserved', always(i18n.t('timerCard.reserved')))
    .with('soon_expired', always(i18n.t('timerCard.soonExpired')))
    .with('expired', always(i18n.t('timerCard.expired')))
    .exhaustive();

export type TimerStateType = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  state: TimerState;
};

type TimerCardType = {
  timer: TimerStateType;
  reservedUntil: string;
  colorSchema: TimerCardColorSchema;
  label?: string;
  dateFormat?: LocaleDateTimeFormat;
};

export const TimerCard = (props: TimerCardType) => {
  const formatDateTime = useDateTimeFormatter();
  const label = props.label ?? getLabel(props.timer.state);
  const formattedDate = formatDateTime(
    props.dateFormat ?? 'dateShort',
    parseDate(props.reservedUntil)
  );

  if (props.timer.state === 'expired') {
    return (
      <CardOo backgroundColor={props.colorSchema.cardBg}>
        <HStack align="center" spacing={5} data-testid="timerCard">
          <Box padding={1}>
            <Icon value="messageChatWarning" size={6} isNotFilled />
          </Box>
          <VStack spacing={1}>
            <Text size="small" alternative color={props.colorSchema.textColor}>
              {label}
            </Text>
            <Text size="xSmall" color={props.colorSchema.textColor}>
              {formattedDate}
            </Text>
          </VStack>
        </HStack>
      </CardOo>
    );
  }

  return (
    <CardOo backgroundColor={props.colorSchema.cardBg}>
      <VStack align="flex-start" spacing={2}>
        <HStack align="center" justify="space-between" width="100%">
          <Text
            size="small"
            alternative
            color={props.colorSchema.textColor}
            data-testid="timerCard"
          >
            {label}
          </Text>
          <Text size="xSmall" color={props.colorSchema.textColor}>
            {i18n.t('timerCard.reservedUntil', {date: formattedDate})}
          </Text>
        </HStack>
        <Timer
          days={props.timer.days}
          hours={props.timer.hours}
          seconds={props.timer.seconds}
          minutes={props.timer.minutes}
          textColor={props.colorSchema.textColor}
        />
      </VStack>
    </CardOo>
  );
};
